// OpenAreaFraction/SplashVeloc are only ever read (GRATE/DROP_GRATE alike)
// when GrateType is GENERIC — any other grate type ignores those two
// tokens even if present; DROP_CURB never reads a throat-angle token at
// all. `writeInletDesigns` mirrors that: those fields are only ever
// EMITTED under the same conditions.
//
// Deliberately unsupported real quirk, documented rather than silently
// mishandled: a real "combination" inlet (COMBO_INLET) is written as TWO
// SEPARATE [INLETS] rows sharing the SAME ID, which the real reader merges
// into one combined design once both arrive — `readInletDesigns` reads
// each row as its own independent single-type design instead; a combo
// design authored elsewhere round-trips as two DISTINCT designs under the
// same displayed name rather than merging.

import { nextAvailableName } from "./nextAvailableName.ts";
import { parseNum } from "./parseNum.ts";
import { writeOrDeleteSection } from "./writeOrDeleteSection.ts";

export const GRATE_TYPES = [
  "P_BAR-50",
  "P_BAR-50x100",
  "P_BAR-30",
  "CURVED_VANE",
  "TILT_BAR-45",
  "TILT_BAR-30",
  "RETICULINE",
  "GENERIC",
] as const;
export type GrateType = (typeof GRATE_TYPES)[number];

export const THROAT_ANGLES = ["HORIZONTAL", "INCLINED", "VERTICAL"] as const;
export type ThroatAngle = (typeof THROAT_ANGLES)[number];

export const PLACEMENTS = ["AUTOMATIC", "ON_GRADE", "ON_SAG"] as const;
export type Placement = (typeof PLACEMENTS)[number];

export type InletDesign =
  | {
      name: string;
      type: "GRATE" | "DROP_GRATE";
      length: number;
      width: number;
      grateType: GrateType;
      openAreaFraction: number;
      splashVelocity: number;
    }
  | {
      name: string;
      type: "CURB" | "DROP_CURB";
      length: number;
      height: number;
      throatAngle: ThroatAngle;
    }
  | { name: string; type: "SLOTTED"; length: number; width: number }
  | { name: string; type: "CUSTOM"; curveName: string };

export interface InletUsage {
  link: string;
  inlet: string;
  node: string;
  numInlets: number;
  pctClogged: number;
  flowLimit: number;
  localDepress: number;
  localWidth: number;
  placement: Placement;
}

export function readInletDesigns(
  raw: ReadonlyMap<string, string[][]>,
  warnings?: Set<string>
): InletDesign[] {
  const designs: InletDesign[] = [];
  for (const row of raw.get("INLETS") ?? []) {
    const name = row[0];
    const type = row[1];
    if (!name || !type) continue;
    const ctx = (field: string) => `[INLETS] "${name}" ${type} ${field}`;
    if (type === "GRATE" || type === "DROP_GRATE") {
      const grateType = (row[4] as GrateType) ?? "GENERIC";
      designs.push({
        name,
        type,
        length: parseNum(row[2], 0, ctx("Length"), warnings),
        width: parseNum(row[3], 0, ctx("Width"), warnings),
        grateType,
        openAreaFraction: parseNum(row[5], 0, ctx("Open Area Fraction"), warnings),
        splashVelocity: parseNum(row[6], 0, ctx("Splash Velocity"), warnings),
      });
    } else if (type === "CURB" || type === "DROP_CURB") {
      designs.push({
        name,
        type,
        length: parseNum(row[2], 0, ctx("Length"), warnings),
        height: parseNum(row[3], 0, ctx("Height"), warnings),
        throatAngle: (row[4] as ThroatAngle) ?? "VERTICAL",
      });
    } else if (type === "SLOTTED") {
      designs.push({
        name,
        type,
        length: parseNum(row[2], 0, ctx("Length"), warnings),
        width: parseNum(row[3], 0, ctx("Width"), warnings),
      });
    } else if (type === "CUSTOM") {
      designs.push({ name, type, curveName: row[2] ?? "" });
    }
  }
  return designs;
}

export function writeInletDesigns(
  raw: ReadonlyMap<string, string[][]>,
  designs: InletDesign[]
): Map<string, string[][]> {
  const rows = designs.map((d): string[] => {
    switch (d.type) {
      case "GRATE":
      case "DROP_GRATE": {
        const row = [d.name, d.type, String(d.length), String(d.width), d.grateType];
        if (d.grateType === "GENERIC") {
          row.push(String(d.openAreaFraction), String(d.splashVelocity));
        }
        return row;
      }
      case "CURB":
        return [d.name, d.type, String(d.length), String(d.height), d.throatAngle];
      case "DROP_CURB":
        return [d.name, d.type, String(d.length), String(d.height)];
      case "SLOTTED":
        return [d.name, d.type, String(d.length), String(d.width)];
      case "CUSTOM":
        return [d.name, d.type, d.curveName];
    }
  });
  return writeOrDeleteSection(raw, "INLETS", rows);
}

export function nextAvailableInletName(existingNames: Set<string>): string {
  return nextAvailableName(existingNames, "IN");
}

export function readInletUsage(
  raw: ReadonlyMap<string, string[][]>,
  warnings?: Set<string>
): InletUsage[] {
  const usage: InletUsage[] = [];
  for (const row of raw.get("INLET_USAGE") ?? []) {
    const link = row[0];
    const inlet = row[1];
    const node = row[2];
    if (!link || !inlet || !node) continue;
    const ctx = (field: string) => `[INLET_USAGE] "${link}" "${inlet}" ${field}`;
    usage.push({
      link,
      inlet,
      node,
      numInlets: parseNum(row[3], 1, ctx("#Inlets"), warnings),
      pctClogged: parseNum(row[4], 0, ctx("%Clogged"), warnings),
      flowLimit: parseNum(row[5], 0, ctx("Qmax"), warnings),
      localDepress: parseNum(row[6], 0, ctx("aLocal"), warnings),
      localWidth: parseNum(row[7], 0, ctx("wLocal"), warnings),
      placement: (row[8] as Placement) ?? "AUTOMATIC",
    });
  }
  return usage;
}

export function writeInletUsage(
  raw: ReadonlyMap<string, string[][]>,
  usage: InletUsage[]
): Map<string, string[][]> {
  const rows = usage.map((u) => [
    u.link,
    u.inlet,
    u.node,
    String(u.numInlets),
    String(u.pctClogged),
    String(u.flowLimit),
    String(u.localDepress),
    String(u.localWidth),
    u.placement,
  ]);
  return writeOrDeleteSection(raw, "INLET_USAGE", rows);
}

export function inletUsageFor(usage: InletUsage[], link: string): InletUsage[] {
  return usage.filter((u) => u.link === link);
}
